import { create } from 'zustand';
import type {
  Gift,
  GiftQuery,
  RecommendationSnapshot,
  ContentPiece,
  CoverTemplate,
  PlanItem,
  ReviewResult,
  Publish,
  PublishChannel,
  ProviderCallResult,
  AskResult
} from '../types';
import { aiMock } from '../services/ai/mock';

interface AIState {
  /* 推荐 */
  lastQuery: GiftQuery | null;
  recommendation: RecommendationSnapshot | null;
  askHistory: Array<{ role: 'user' | 'assistant'; text: string; ts: number }>;

  /* 内容 */
  contents: ContentPiece[];
  cover: CoverTemplate | null;

  /* 发布 */
  publish: Publish | null;

  /* 复盘 / 计划 */
  review: ReviewResult | null;
  plan: PlanItem[];

  /* Actions */
  ask: (raw: string) => Promise<ProviderCallResult<AskResult>>;
  recommend: (q: GiftQuery) => Promise<ProviderCallResult<RecommendationSnapshot>>;
  generateContent: (giftId: string, style: ContentPiece['type']) => Promise<ProviderCallResult<ContentPiece[]>>;
  generateCover: (giftId: string, style: CoverTemplate['style']) => Promise<ProviderCallResult<CoverTemplate>>;
  buildPublish: (recId: string, contentId: string, coverId: string, channels: PublishChannel[]) => Publish;
  confirmPublish: () => void;
  loadReview: () => Promise<ProviderCallResult<ReviewResult>>;
  loadPlan: () => Promise<ProviderCallResult<PlanItem[]>>;
  reset: () => void;
}

export const useAIStore = create<AIState>((set, get) => ({
  lastQuery: null,
  recommendation: null,
  askHistory: [],
  contents: [],
  cover: null,
  publish: null,
  review: null,
  plan: [],

  ask: async (raw) => {
    const res = await aiMock.ask(raw);
    if (res.ok && res.data) {
      const data = res.data;  // 捕获到 const，让 TS narrow 通过 set() 闭包传递
      set((s) => ({ askHistory: [...s.askHistory, { role: 'user', text: raw, ts: Date.now() }] }));
      if (data.type === 'recommend') {
        set({ recommendation: data.recommendation, lastQuery: data.recommendation.query });
        set((s) => ({ askHistory: [...s.askHistory, { role: 'assistant', text: `为你推荐 ${data.recommendation.gifts.length} 份合适的礼物`, ts: Date.now() }] }));
      } else if (data.type === 'greeting') {
        set((s) => ({ askHistory: [...s.askHistory, { role: 'assistant', text: data.message, ts: Date.now() }] }));
      }
    }
    return res;
  },

  recommend: async (q) => {
    const res = await aiMock.recommend(q);
    if (res.ok && res.data) set({ recommendation: res.data, lastQuery: q });
    return res;
  },

  generateContent: async (giftId, style) => {
    const res = await aiMock.generateContent(giftId, style);
    if (res.ok && res.data) set({ contents: res.data });
    return res;
  },

  generateCover: async (giftId, style) => {
    const res = await aiMock.generateCover(giftId, style);
    if (res.ok && res.data) set({ cover: res.data });
    return res;
  },

  buildPublish: (recId, contentId, coverId, channels) => {
    const publish: Publish = {
      id: `pub_${Date.now()}`,
      recommendation_id: recId,
      content_id: contentId,
      cover_id: coverId,
      channels,
      scheduled_at: null,
      status: 'CREATED',
      quality_score: 92,
      created_at: Date.now(),
      published_at: null
    };
    set({ publish });
    return publish;
  },

  confirmPublish: () => {
    const p = get().publish;
    if (!p) return;
    set({ publish: { ...p, status: 'PAID', published_at: Date.now() } });
  },

  loadReview: async () => {
    const res = await aiMock.review('u_local');
    if (res.ok && res.data) set({ review: res.data });
    return res;
  },

  loadPlan: async () => {
    const res = await aiMock.nextPlan('u_local');
    if (res.ok && res.data) set({ plan: res.data });
    return res;
  },

  reset: () => set({
    lastQuery: null,
    recommendation: null,
    askHistory: [],
    contents: [],
    cover: null,
    publish: null,
    review: null,
    plan: []
  })
}));
